/**
 * DOM replica of the overlays a card carries on the table: the ATK / DEF
 * chips at the printed-bottom corners (plus the Movement +N satellite
 * above DEF), the status badge column at the top-left, and the attached-
 * equipment mini stack centered on the host. Every size derives from the
 * same world-unit geometry cardMesh.js draws with, expressed as a fraction
 * of the portrait card width, so a thumbnail at any size reads like the
 * card on the board.
 *
 * Purely presentational: which chips and badges a card shows is decided
 * by the board controllers (equipActions.displayStatChips /
 * displayStatusKeys) and handed in as data.
 *
 * Props:
 *   card      instance (equippedCards)
 *   statuses  string[] display status keys
 *   chips     [{ stat: 'atk' | 'def' | 'mov', value, delta }]
 *   w, h      rendered card box in cqw (landscape for sites)
 *   isSite    box is landscape; the portrait width is then `h`
 */
import { Sword, Shield, Footprints } from 'lucide-react';
import { CONTENT_BG_DEFAULT, RADIUS, innerRadius } from '../../lib/medievalTheme';
import { CARD_CORNER_RATIO } from '../../lib/cardCorner';
import {
  CARD_WIDTH,
  STAT_HUD_W,
  STAT_HUD_H,
  STAT_PAD,
  MOV_HUD_SCALE,
  MOV_HUD_GAP,
  STATUS_BADGE_SIZE,
  STATUS_BADGE_VISIBLE,
  STATUS_BADGE_GAP,
  STATUS_EFFECTS,
} from '../../utils/game/cardMesh';
import { resolveLocalImageUrl } from '../../utils/localApi';
import { k, t } from '../../localization/t';

// Stone surface recipe shared with the attack prompt panel: dark base,
// stone @ soft-light, the codex content textures underneath.
export const STONE_BG = `linear-gradient(180deg, rgba(40,30,15,0.55) 0%, rgba(15,10,4,0.85) 100%), ${CONTENT_BG_DEFAULT}`;
export const STONE_BLEND = 'normal, soft-light, normal, normal, normal, normal';

const STATUS_BY_KEY = new Map(STATUS_EFFECTS.map((e) => [e.key, e]));

// World units → fraction of the portrait card width.
const frac = (worldUnits) => worldUnits / CARD_WIDTH;

// Same hues as STAT_COLORS_HUD in cardMesh.js.
const CHIP_ICON = {
  atk: { Icon: Sword, color: 'rgba(245,158,11,0.95)' },
  def: { Icon: Shield, color: 'rgba(239,68,68,0.95)' },
  mov: { Icon: Footprints, color: 'rgba(132,204,255,0.95)' },
};

const CHIP_LABEL = { atk: k('Attack'), def: k('Defense'), mov: k('Movement') };

// applyPillTransforms: ATK bottom-left, DEF bottom-right, the Movement
// satellite right-aligned above DEF.
function chipPosition(stat, unit) {
  const pad = unit * frac(STAT_PAD);
  if (stat === 'atk') return { left: `${pad}cqw`, bottom: `${pad}cqw` };
  if (stat === 'def') return { right: `${pad}cqw`, bottom: `${pad}cqw` };
  return { right: `${pad}cqw`, bottom: `${pad + unit * frac(STAT_HUD_H + MOV_HUD_GAP)}cqw` };
}

function StatChip({ stat, value, delta, unit }) {
  const scale = stat === 'mov' ? MOV_HUD_SCALE : 1;
  const w = unit * frac(STAT_HUD_W) * scale;
  const h = unit * frac(STAT_HUD_H) * scale;
  // Buff / debuff tint + rim + text colour mirror createStatTexture.
  const tint = delta > 0 ? '47,155,79' : delta < 0 ? '168,42,42' : null;
  const tintAlpha = Math.min(0.32, 0.2 + Math.abs(delta) * 0.02);
  const rim = delta > 0
    ? 'rgba(170,165,75,0.95)'
    : delta < 0 ? 'rgba(190,130,70,0.95)' : 'rgba(180,140,60,0.95)';
  const text = delta > 0 ? '#ecf0c4' : delta < 0 ? '#f5dab2' : '#f5e4b6';
  const { Icon, color } = CHIP_ICON[stat];
  const display = stat === 'mov' && value > 0 ? `+${value}` : String(value);
  return (
    <div
      aria-label={t(CHIP_LABEL[stat])}
      data-delta={delta > 0 ? 'buff' : delta < 0 ? 'debuff' : 'base'}
      style={{
        position: 'absolute',
        ...chipPosition(stat, unit),
        width: `${w}cqw`,
        height: `${h}cqw`,
        // The chip's outer corner shares the card corner's center, like
        // the in-canvas pill (CARD_CORNER_RADIUS - STAT_PAD).
        borderRadius: innerRadius(`${unit * CARD_CORNER_RATIO}cqw`, `${unit * frac(STAT_PAD)}cqw`),
        backgroundColor: '#0c0a08',
        backgroundImage: tint
          ? `linear-gradient(rgba(${tint},${tintAlpha}), rgba(${tint},${tintAlpha})), ${STONE_BG}`
          : STONE_BG,
        backgroundBlendMode: tint ? `screen, ${STONE_BLEND}` : STONE_BLEND,
        boxShadow: `0 0 0 1px ${rim}, inset 0 0 0 1px rgba(255,222,152,0.32)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: `${h * 0.12}cqw`,
        color: text,
        fontWeight: 700,
        fontSize: `${h * 0.58}cqw`,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
        textShadow: '0 1px 2px rgba(0,0,0,0.65)',
      }}
    >
      <Icon
        style={{ width: `${h * 0.5}cqw`, height: `${h * 0.5}cqw`, flex: '0 0 auto' }}
        strokeWidth={1.5}
        color={color}
        aria-hidden="true"
      />
      <span>{display}</span>
    </div>
  );
}

// Glass-dome badge matching createStatusBadgeTexture: dark radial base,
// the status colour lit from the top, a sheen, a grounding shadow, and a
// gold rim.
function StatusBadge({ effect, size }) {
  return (
    <div
      title={t(effect.label)}
      style={{
        width: `${size}cqw`,
        height: `${size}cqw`,
        flex: '0 0 auto',
        borderRadius: '50%',
        backgroundImage: [
          'radial-gradient(ellipse 70% 40% at 50% 28%, rgba(255,255,255,0.35), rgba(255,255,255,0) 100%)',
          'radial-gradient(ellipse 80% 45% at 50% 75%, rgba(0,0,0,0.4), rgba(0,0,0,0) 100%)',
          `linear-gradient(180deg, ${effect.color}66 0%, ${effect.color}66 60%, transparent 100%)`,
          'radial-gradient(circle at 38% 35%, #3a3530 0%, #201c18 55%, #0e0c0a 100%)',
        ].join(', '),
        boxShadow: '0 0 0 1px rgba(140,110,50,0.9), inset 0 0 0 1px rgba(220,180,80,0.35)',
        color: '#fff',
        fontWeight: 700,
        fontSize: `${size * 0.3}cqw`,
        lineHeight: 1,
        textShadow: '0 1px 3px rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {effect.abbr}
    </div>
  );
}

function StatusColumn({ statuses, unit }) {
  const list = (statuses || []).map((key) => STATUS_BY_KEY.get(key)).filter(Boolean);
  if (list.length === 0) return null;
  const size = unit * frac(STATUS_BADGE_VISIBLE);
  // buildStatusBadges centers the first badge at pad + SIZE/2 from the
  // corner and steps by SIZE + GAP; the drawn quad is VISIBLE wide.
  const inset = unit * frac(STAT_PAD + 0.1 + (STATUS_BADGE_SIZE - STATUS_BADGE_VISIBLE) / 2);
  const gap = unit * frac(STATUS_BADGE_SIZE + STATUS_BADGE_GAP - STATUS_BADGE_VISIBLE);
  return (
    <div style={{
      position: 'absolute',
      left: `${inset}cqw`,
      top: `${inset}cqw`,
      display: 'flex',
      flexDirection: 'column',
      gap: `${gap}cqw`,
    }}>
      {list.map((effect) => <StatusBadge key={effect.key} effect={effect} size={size} />)}
    </div>
  );
}

// Mirrors EquipmentStacks + `.equipment-link-badge` in index.css: a stack
// sized to 42% of the host's rendered width, later cards fanned by 12%
// and 6° per step.
function EquipmentStack({ equippedCards, w }) {
  const list = equippedCards || [];
  if (list.length === 0) return null;
  const n = list.length;
  const size = `max(1.4cqw, ${w * 0.42}cqw)`;
  const long = `calc(${size} * 88.9 / 63.5)`;
  return (
    <div
      title={n > 1 ? t('{n} cards attached', { n }) : t('Card attached')}
      style={{ position: 'absolute', left: '50%', top: '50%', width: size, height: size }}
    >
      {list.map((eq, i) => {
        const offset = (i - (n - 1) / 2) * 12;
        const tilt = (i - (n - 1) / 2) * 6;
        return (
          <div
            key={eq.id}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: eq.isSite ? long : size,
              height: eq.isSite ? size : long,
              transform: `translate(-50%, -50%) translateX(${offset}%) rotate(${tilt}deg)`,
              zIndex: i,
              borderRadius: eq.isSite ? RADIUS.cardLandscape : RADIUS.card,
              border: '1px solid rgba(212,168,67,0.55)',
              boxShadow: '0 4px 10px rgba(0,0,0,0.6)',
              background: '#0c0a08',
              overflow: 'hidden',
            }}
          >
            <img
              src={resolveLocalImageUrl(eq.imageUrl || '')}
              alt={eq.name || ''}
              title={eq.name || ''}
              draggable={false}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </div>
        );
      })}
    </div>
  );
}

export default function BoardCardOverlays({ card, statuses, chips, w, h, isSite }) {
  const unit = isSite ? h : w;
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      <StatusColumn statuses={statuses} unit={unit} />
      <EquipmentStack equippedCards={card?.equippedCards} w={w} />
      {(chips || []).map((chip) => (
        <StatChip key={chip.stat} stat={chip.stat} value={chip.value} delta={chip.delta} unit={unit} />
      ))}
    </div>
  );
}
